import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import { gsap } from "./lib/gsap";
import { Toaster } from "sonner";
import Navbar from "./components/Navbar";
import VideoIntro from "./components/sections/VideoIntro";
import HeroSection from "./components/sections/HeroSection";
import AboutSection from "./components/sections/AboutSection";
import ProjectsSection from "./components/sections/ProjectsSection";
import WorkExperienceSection from "./components/sections/WorkExperienceSection";
import EducationSection from "./components/sections/EducationSection";
import ContactFooterSection from "./components/sections/ContactFooterSection";
import ScreenLoader from "./components/sections/ScreenLoader";

// Snap positions: 0=video, 1=hero, 2=about, 3=projects, 4=experience, 5=education, 6=contact
const TOTAL = 7;
const PROJECTS_IDX = 3;
const EXPERIENCE_IDX = 4;
const EDUCATION_IDX = 5;

// Number of sub-slides per section (defaults to 1 = no internal steps)
const SUBSLIDES = { [EXPERIENCE_IDX]: 2, [EDUCATION_IDX]: 2 };

// Shared horizontal-scroll state for projects (0..1)
export const projectsScroll = { value: 0 };

export default function App() {
  const mainRef = useRef(null);
  const idxRef = useRef(0);
  const busyRef = useRef(false);
  const tweenRef = useRef(null);
  const subRef = useRef(0); // current sub-slide within a section
  const wheelLockRef = useRef(false);
  const wheelIdleRef = useRef(null);
  const [showLoader, setShowLoader] = useState(true);
  const [projX, setProjX] = useState(0);
  const [expStep, setExpStep] = useState(0);
  const [eduStep, setEduStep] = useState(0);

  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;

    function setSubForCurrent(v) {
      subRef.current = v;
      if (idxRef.current === EXPERIENCE_IDX) setExpStep(v);
      if (idxRef.current === EDUCATION_IDX) setEduStep(v);
    }

    function goTo(idx) {
      if (idx >= TOTAL) return;
      if (idx < 0) return;
      if (idx === idxRef.current || busyRef.current) return;
      // Reset internal states when leaving section
      if (idxRef.current === PROJECTS_IDX && idx !== PROJECTS_IDX) { projectsScroll.value = 0; setProjX(0); }
      // Reset sub-slide when entering a new section
      const newSubMax = (SUBSLIDES[idx] || 1) - 1;
      // If moving forward, start at 0; if moving backward, start at last sub-slide
      subRef.current = idx > idxRef.current ? 0 : newSubMax;
      if (idx === EXPERIENCE_IDX) setExpStep(subRef.current);
      if (idx === EDUCATION_IDX) setEduStep(subRef.current);
      // Reset other sections' sub state
      if (idx !== EXPERIENCE_IDX) setExpStep(0);
      if (idx !== EDUCATION_IDX) setEduStep(0);
      if (idx === PROJECTS_IDX) { projectsScroll.value = 0; setProjX(0); }
      idxRef.current = idx;
      busyRef.current = true;
      // Attempt this synchronously, inside the same call stack as the
      // originating user gesture (touchend/wheel/keydown), not from the
      // async scroll listener the Contact section itself uses. Strict
      // mobile browsers (notably iOS Safari) can refuse video.play() once
      // it's a few ticks removed from a real user gesture, even for a
      // muted video, which made autoplay unreliable on some devices.
      if (idx === TOTAL - 1) window.__playContactVideo?.();
      tweenRef.current?.kill();
      tweenRef.current = gsap.to(el, {
        scrollTop: idx * window.innerHeight, duration: 0.9, ease: "power3.inOut",
        onComplete: () => setTimeout(() => (busyRef.current = false), 400),
      });
    }

    function onWheel(e) {
      e.preventDefault();
      if (busyRef.current) return;
      const forward = e.deltaY > 0;
      const cur = idxRef.current;

      // Projects horizontal hijack (continuous, NO debounce for smoothness)
      if (cur === PROJECTS_IDX) {
        // Normalise deltaY across browsers/trackpads to a smooth step
        const raw = Math.max(-250, Math.min(250, e.deltaY));
        const step = raw / 300; // fast, smooth horizontal step
        const next = projectsScroll.value + step;
        if (next > 0 && next < 1) {
          projectsScroll.value = Math.max(0, Math.min(1, next));
          setProjX(projectsScroll.value);
          return;
        }
        if (next >= 1 && forward) { projectsScroll.value = 1; setProjX(1); goTo(cur + 1); return; }
        if (next <= 0 && !forward) { projectsScroll.value = 0; setProjX(0); goTo(cur - 1); return; }
      }

      // Section-advance only (not projects horizontal): lock out further
      // advances for the rest of this physical gesture, not just for a
      // fixed time window. A trackpad sends a continuous stream of wheel
      // events for a single swipe, often spanning well past a short
      // time-based debounce, which let one swipe skip through both
      // Experience/Education sub-slides at once. Only unlock once the wheel
      // has gone idle (no events) for a bit, which reliably marks the end
      // of the gesture, mirroring the discrete one-swipe-one-step touch fix.
      clearTimeout(wheelIdleRef.current);
      wheelIdleRef.current = setTimeout(() => { wheelLockRef.current = false; }, 220);
      if (wheelLockRef.current) return;
      wheelLockRef.current = true;

      // Sub-slide sections (discrete steps)
      const subMax = (SUBSLIDES[cur] || 1) - 1;
      if (subMax > 0) {
        if (forward && subRef.current < subMax) {
          setSubForCurrent(subRef.current + 1);
          return;
        }
        if (!forward && subRef.current > 0) {
          setSubForCurrent(subRef.current - 1);
          return;
        }
        goTo(cur + (forward ? 1 : -1));
        return;
      }

      goTo(cur + (forward ? 1 : -1));
    }

    let touchY = 0;
    function onTouchStart(e) { touchY = e.touches[0].clientY; }
    // Without this, real touch devices natively scroll .main-scroller
    // alongside (and independently of) the swipe logic below, since it's a
    // genuinely scrollable container and touchmove is never blocked. That
    // native scroll can jump by however far the physical gesture and its
    // momentum carry it, bypassing the horizontal-carousel and sub-slide
    // step logic entirely, one swipe lands wherever native momentum
    // happens to stop rather than one deliberate step. Blocking it here
    // (mirroring the preventDefault already done for wheel) makes every
    // touch gesture go through the same controlled goTo()/step path that
    // desktop already uses. Form fields are exempted so typing still works.
    function onTouchMove(e) {
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      e.preventDefault();
    }
    function onTouchEnd(e) {
      const dy = touchY - e.changedTouches[0].clientY;
      if (Math.abs(dy) < 40 || busyRef.current) return;
      const forward = dy > 0;
      const cur = idxRef.current;
      if (cur === PROJECTS_IDX) {
        // Divisor tuned so one discrete swipe advances roughly one card's
        // width (touch has no continuous "ticks" like wheel does, so a
        // single gesture shouldn't be able to blow through the whole
        // carousel the way a fast wheel flick's per-tick step can).
        const step = dy / 1500;
        const next = projectsScroll.value + step;
        if (next > 0 && next < 1) { projectsScroll.value = Math.max(0, Math.min(1, next)); setProjX(projectsScroll.value); return; }
        if (next >= 1 && forward) { goTo(cur + 1); return; }
        if (next <= 0 && !forward) { goTo(cur - 1); return; }
      }
      const subMax = (SUBSLIDES[cur] || 1) - 1;
      if (subMax > 0) {
        if (forward && subRef.current < subMax) { setSubForCurrent(subRef.current + 1); return; }
        if (!forward && subRef.current > 0) { setSubForCurrent(subRef.current - 1); return; }
        goTo(cur + (forward ? 1 : -1)); return;
      }
      goTo(cur + (forward ? 1 : -1));
    }
    function onScroll() { idxRef.current = Math.round(el.scrollTop / window.innerHeight); }
    function onKeyDown(e) {
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (["ArrowDown", "PageDown", " "].includes(e.key)) {
        e.preventDefault();
        const cur = idxRef.current;
        const subMax = (SUBSLIDES[cur] || 1) - 1;
        if (subMax > 0 && subRef.current < subMax) { setSubForCurrent(subRef.current + 1); return; }
        goTo(cur + 1);
      }
      if (["ArrowUp", "PageUp"].includes(e.key)) {
        e.preventDefault();
        const cur = idxRef.current;
        const subMax = (SUBSLIDES[cur] || 1) - 1;
        if (subMax > 0 && subRef.current > 0) { setSubForCurrent(subRef.current - 1); return; }
        goTo(cur - 1);
      }
      if (e.key === "Home") { e.preventDefault(); goTo(0); }
      if (e.key === "End") { e.preventDefault(); goTo(TOTAL - 1); }
    }

    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("keydown", onKeyDown);
    // Expose goTo globally for Navbar / other components
    window.__portfolioGoTo = goTo;
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("keydown", onKeyDown);
      tweenRef.current?.kill();
      clearTimeout(wheelIdleRef.current);
    };
  }, []);

  return (
    <>
      {showLoader && <ScreenLoader onDismiss={() => setShowLoader(false)} />}
      <Navbar />
      <main ref={mainRef} className="main-scroller" style={{ height: "100vh", overflowY: "scroll", overscrollBehavior: "none" }}>
        <div>
          <VideoIntro />
          <HeroSection />
          <AboutSection />
          <ProjectsSection horizontalProgress={projX} />
          <WorkExperienceSection subSlide={expStep} />
          <EducationSection subSlide={eduStep} />
          <ContactFooterSection />
        </div>
      </main>
      <Toaster theme="dark" position="top-center" richColors />
    </>
  );
}
